class SwiperSlider extends HTMLElement {
  constructor() {
    super();
    this.swiper = null;
    this.config = null;
    this.thumbsSwiper = null;
    this.observer = null;
    this.isVisible = false;

    // Get breakpoint values
    this.breakpointMax = parseInt(this.getAttribute('data-breakpoint-max')) || null;
    this.breakpointMin = parseInt(this.getAttribute('data-breakpoint-min')) || null;
  }

  debounce(fn, delay = 100) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn.apply(this, args), delay);
    };
  }

  connectedCallback() {
    // Différer l'initialisation pour améliorer l'INP
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => {
        this.loadConfig();
        this.initDetailsListeners();
        this.setupObserver();
        this.setupResizeListener();
      });
    } else {
      setTimeout(() => {
        this.loadConfig();
        this.initDetailsListeners();
        this.setupObserver();
        this.setupResizeListener();
      }, 0);
    }
  }

  setupResizeListener() {
    this.debouncedResizeHandler = this.debounce(this.handleResize.bind(this), 150); // Augmenter le délai
    window.addEventListener('resize', this.debouncedResizeHandler, { passive: true });
  }

  setupObserver() {
    // Vérifier si l'API IntersectionObserver est disponible
    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(this.handleIntersection.bind(this), {
        rootMargin: '100px', // Préchargement 200px avant que l'élément soit visible
        threshold: 0.01,
      });

      this.observer.observe(this);
    } else {
      // Fallback pour les navigateurs qui ne supportent pas IntersectionObserver
      this.initSwiper();
    }
  }

  handleIntersection(entries) {
    const entry = entries[0];

    if (entry.isIntersecting && !this.isVisible) {
      this.isVisible = true;

      // Vérifier si Swiper est disponible
      if (typeof Swiper === 'undefined' && typeof window.loadSwiper === 'function') {
        window.loadSwiper().then(() => {
          this.initSwiper();
        });
      } else {
        this.initSwiper();
      }

      // Arrêter d'observer une fois que le composant est visible
      if (this.observer) {
        this.observer.unobserve(this);
      }
    }
  }

  initDetailsListeners() {
    // Find all details elements inside the swiper
    const detailsElement = this.closest('details');

    if (detailsElement) {
      // Store bound function for cleanup
      this.handleDetailsToggle = (event) => {
        // If the details element is open, reload the swiper
        if (detailsElement.open) {
          // Utiliser requestAnimationFrame pour optimiser le reload
          requestAnimationFrame(() => {
            this.reload();
          });
        }
      };

      // For each details element, add a toggle event listener
      detailsElement.addEventListener('toggle', this.handleDetailsToggle);
    }
  }

  disconnectedCallback() {
    this.destroySwiper();
    window.removeEventListener('resize', this.debouncedResizeHandler);

    // Nettoyer l'observer
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    // Clean up details event listeners if needed
    const detailsElement = this.closest('details');
    if (detailsElement) {
      detailsElement.removeEventListener('toggle', this.handleDetailsToggle);
    }
  }

  loadConfig() {
    const script = this.querySelector('script[type="application/json"]');
    if (script) {
      try {
        this.config = JSON.parse(script.textContent);
      } catch (e) {
        console.error('Invalid JSON configuration in <swiper-slider>: ', e);
      }
    } else {
      this.config = {};
    }
  }

  reload() {
    this.destroySwiper();

    // Utiliser requestAnimationFrame pour optimiser le reload
    requestAnimationFrame(() => {
      // Si le composant est déjà visible ou si l'observer n'est pas utilisé
      if (this.isVisible || !this.observer) {
        this.initSwiper(true); // Pass true to force initialization
      } else {
        // Réinitialiser l'observer pour détecter la visibilité
        this.setupObserver();
      }
    });
  }

  shouldInitializeSwiper() {
    const windowWidth = window.innerWidth;

    if (this.breakpointMax && windowWidth >= this.breakpointMax) return false;
    if (this.breakpointMin && windowWidth < this.breakpointMin) return false;
    return true;
  }

  initSwiper(forceInit) {
    // Don't initialize if outside of breakpoint range
    if (!forceInit && !this.shouldInitializeSwiper()) {
      return;
    }

    const defaultConfig = {
      slidesPerView: 1,
      spaceBetween: 0,
      loop: false,
      draggable: true,
      allowTouchMove: true,
      grabCursor: true,
      mousewheel: {
        forceToAxis: true,
        releaseOnEdges: true,
      },
      keyboard: {
        enabled: true,
      },
    };

    const swiperConfig = {
      ...defaultConfig,
      ...this.config,
    };

    // Initialize thumbs swiper if thumbs config exists
    if (this.config.thumbs) {
      const thumbsElement = document.querySelector(this.config.thumbs.el);

      if (thumbsElement) {
        const thumbsConfig = {
          watchSlidesProgress: true,
          slideToClickedSlide: true,
          ...(this.config.thumbs.config || {}),
        };

        this.thumbsSwiper = new Swiper(thumbsElement, thumbsConfig);

        swiperConfig.thumbs = {
          swiper: this.thumbsSwiper,
        };
      }
    }

    const slidesWrapper = this.querySelector('.swiper-wrapper');

    if (!slidesWrapper) {
      console.error('Cannot find .swiper-wrapper element');
      return;
    }

    const containerWidth = this.clientWidth;
    const slidesWidth = slidesWrapper.scrollWidth;

    // Skip initialization if all slides are visible, unless forceInit is true
    if (!forceInit && slidesWidth <= containerWidth) {
      return;
    }

    // Initialize main Swiper avec requestAnimationFrame pour éviter de bloquer l'INP
    requestAnimationFrame(() => {
      try {
        // Vérifier si Swiper est disponible
        if (typeof Swiper !== 'undefined') {
          this.swiper = new Swiper(this, swiperConfig);

          // Émettre un événement personnalisé pour indiquer que Swiper est prêt
          this.dispatchEvent(
            new CustomEvent('swiper:ready', {
              bubbles: true,
              detail: { swiper: this.swiper },
            }),
          );
        } else {
          console.error('Swiper is not defined. Make sure it is loaded before initializing.');
        }
      } catch (e) {
        console.error('Swiper initialization failed: ', e);
      }
    });

    const fractionEl = this.querySelector('.swiper-pagination-fraction');
    if (fractionEl) {
      const updateFraction = () => {
        // Si en mode loop, le nombre total est recalculé en excluant les slides dupliquées
        const current = this.swiper.realIndex + 1;
        fractionEl.textContent = current + ' / ' + this.swiper.slides.length;
      };

      // MàJ dès l'initialisation et à chaque changement de slide
      this.swiper.on('init', updateFraction);
      this.swiper.on('slideChange', updateFraction);
      // Pour forcer la première mise à jour
      updateFraction();
    }
  }

  destroySwiper() {
    if (this.thumbsSwiper) {
      this.thumbsSwiper.destroy(true, true);
      this.thumbsSwiper = null;
    }
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  }

  handleResize() {
    // Utiliser requestAnimationFrame pour optimiser les opérations de resize
    requestAnimationFrame(() => {
      const prevConfig = this.config;
      this.loadConfig();

      // Check if we should initialize or destroy based on breakpoints
      const shouldInit = this.shouldInitializeSwiper();

      if (shouldInit && !this.swiper && this.isVisible) {
        // Initialize if we're within breakpoints, swiper doesn't exist, and component is visible
        this.initSwiper();
      } else if (!shouldInit && this.swiper) {
        // Destroy if we're outside breakpoints and swiper exists
        this.destroySwiper();
      } else if (shouldInit && this.swiper && JSON.stringify(prevConfig) !== JSON.stringify(this.config)) {
        // Reinitialize if config has changed and we're within breakpoints
        this.destroySwiper();
        if (this.isVisible) {
          this.initSwiper();
        } else {
          this.setupObserver();
        }
      }
    });
  }
}

customElements.define('swiper-slider', SwiperSlider);
